import type { FC } from "react";
import DateRangePicker from "src/components/reusable/DateRangePicker/DateRangePicker";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import TablePagePagination from "src/components/common/TablePagePagination/TablePagePagination";
import NotificationStateDisplay from "src/components/reusable/NotificationStateDisplay/NotificationStateDisplay";
import NotificationListRow from "../Components/NotificationListRow";
import { useAllNotifications } from "../Hooks/UseAllNotifications";
import "../Styles/NotificationPage.scss";
import "../Styles/AllNotifications.scss";

const AllNotifications: FC = () => {
  const {
    t,
    i18n,
    notifications,
    selectedFilter,
    isLoading,
    error,
    dateRange,
    currentPage,
    totalPages,
    highlightedId,
    filterOptions,
    handleFilterChange,
    handleDateRangeChange,
    handleNotificationClick,
    goToPreviousPage,
    goToNextPage,
  } = useAllNotifications();

  const renderListBody = () => {
    if (isLoading) {
      return null;
    }
    if (error) {
      return (
        <NotificationStateDisplay
          variant="error"
          title={t("allNotifications.errors.loadFailedTitle", {
            defaultValue: "Something went wrong!",
          })}
          message={error}
        />
      );
    }
    if (notifications.length === 0) {
      return (
        <NotificationStateDisplay
          variant="empty"
          title={t("allNotifications.emptyTitle", { defaultValue: "No Notifications" })}
          message={t("allNotifications.noNotifications")}
        />
      );
    }

    return notifications.map(notification => (
      <NotificationListRow
        key={notification.notification_id}
        notification={notification}
        isHighlighted={notification.notification_id === highlightedId}
        unreadLabel={t("allNotifications.unread", { defaultValue: "Unread" })}
        locale={i18n.language === "ar" ? "ar" : "en-US"}
        onClick={handleNotificationClick}
      />
    ));
  };

  return (
    <div className="notifications-container all-notifications-page">
      <h1 className="notifications-page-title">
        {t("allNotifications.title", { defaultValue: "All Notifications" })}
      </h1>
      <span className="notifications-page-description">
        {t("allNotifications.subtitle", {
          defaultValue:
            "Stay updated on sales, marketing, social media, and website performance insights.",
        })}
      </span>

      <div className="all-notifications-toolbar">
        <StyledDropdown
          className="filter-dropdown all-notifications-filter-select"
          popupClassName="filter-dropdown-menu"
          value={selectedFilter}
          onChange={handleFilterChange}
          options={filterOptions}
        />

        <div className="notification-date-range-wrapper">
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={handleDateRangeChange}
          />
        </div>
      </div>

      <div className="notifications-list all-notifications-list">
        {renderListBody()}
      </div>

      {!isLoading && notifications.length > 0 && (
        <TablePagePagination
          className="all-notifications-pagination"
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={goToPreviousPage}
          onNext={goToNextPage}
        />
      )}
    </div>
  );
};

export default AllNotifications;
